import { Component, Element, Event, EventEmitter, h, Listen, Method, Prop, State, Watch } from '@stencil/core';
import Bind from 'bind-decorator';

import { ZdsInputElement } from '../../types';
import { AriaObject } from '../../utils/aria-object';
import { generateUniqueUuid } from '../../utils/generate-unique-uuid';
import { onGenericSubInputPaste } from '../../utils/generic-sub-input-events';
import {
	getDefaultMessage,
	ValidationEvent,
	ValidationMessages,
	ValidationReasons,
} from '../../utils/input-validation';
import { LocaleService } from '../zds-config/locale-service';
import { ForceInvalidType } from '../zds-input-text/force-invalid-type';

@Component({
	shadow: true,
	styleUrl: 'zds-input-number.scss',
	tag: 'zds-input-number',
})
export class ZdsInputNumber implements ZdsInputElement {
	decimalSeparator: string;
	htmlInputElement: HTMLInputElement;
	inputElement: HTMLZdsInputTextElement;
	thousandSeparator: string;

	@Element() hostElement: HTMLElement;

	@Event() zdsBlur: EventEmitter;
	@Event() zdsFocus: EventEmitter;
	@Event({ bubbles: true }) zdsChange: EventEmitter;
	@Event({ bubbles: true }) zdsClick: EventEmitter; // Internal, not documented
	@Event({ bubbles: true }) zdsInput: EventEmitter;
	@Event({ bubbles: true }) zdsInputDidUpdate: EventEmitter; // Allows handling of child event via onZdsInputDidUpdate attribute
	@Event({ bubbles: true }) zdsFormDoSubmit: EventEmitter; // Allows handling of child event via onZdsFormDoSubmit attribute
	@Event() zdsValidate: EventEmitter<ValidationEvent>;

	@Prop() autocomplete: string;
	@Prop() componentTabIndex: number = 0;
	@Prop() clearable: boolean = false;
	@Prop() currency: string;
	@Prop() disabled: boolean;
	@Prop() fitHeight: boolean = false;
	@Prop() forceInvalid: ForceInvalidType = ForceInvalidType.None; // Intentionally not documented; used to force invalid state without error message from a parent component
	@Prop() includeInForm: boolean = true; // Internal prop for not showing the input in the form result
	@Prop() inputAria: AriaObject | string;
	@Prop() instantSave: boolean | 'no-border';
	@Prop() max: number;
	@Prop() maxlength: number;
	@Prop() maxDecimalPlaces: number = 12;
	@Prop() min: number;
	@Prop() minlength: number;
	@Prop() minDecimalPlaces: number = 0;
	@Prop() noAsterisk: boolean = false;
	@Prop() name: string;
	@Prop() label: string;
	@Prop() pattern: string;
	@Prop() placeholder: string;
	@Prop() plaintext: boolean;
	@Prop() readonly: boolean;
	@Prop() required: boolean;
	@Prop() separateThousands: boolean;
	@Prop() silentErrors: boolean = false; // Internal prop used for hiding the validation messages if needed.
	@Prop({ mutable: true }) value: string = '';
	@Prop({ reflect: true }) zdsId: string = generateUniqueUuid('zds-input-number');

	@State() config: HTMLZdsConfigElement;

	@Listen('zdsLocaleChange', { target: 'window' })
	onZdsLocaleChange() {
		const oldDecimalSeparator = this.decimalSeparator;
		this.updateSeparators();

		// If the decimal and thousands separators have switched with the locale, apply this
		if (this.decimalSeparator !== oldDecimalSeparator) {
			this.value = this.value
				.replace(/,/g, '{PERIOD}')
				.replace(/\./g, ',')
				.replace(/\{PERIOD\}/g, '.');
		}
	}

	@Watch('value')
	onValueChange(newValue: string) {
		this.inputElement.value = newValue;
	}

	@Method()
	async addValidationMessages(messages: ValidationMessages) {
		await this.inputElement.addValidationMessages(messages);
	}

	@Method()
	async focusComponent() {
		await this.inputElement.focusComponent();
	}

	@Method()
	async select() {
		await this.inputElement.select();
	}

	@Method()
	async reset() {
		await this.inputElement.reset();
	}

	@Method()
	async validate(forceTouched?: boolean) {
		return this.inputElement.validate(forceTouched);
	}

	@Bind
	applyDecimalFormatter(value: string): string {
		const valueAsNumber = this.parseFormattedNumber(value);

		// If there is no value return an empty string to let the placeholder render
		if (valueAsNumber === null) return '';

		const numberWithMaxDecimals = parseFloat(valueAsNumber.toFixed(this.maxDecimalPlaces));

		// Min and max fraction digits should also be applied when the user is editing
		const permanentNumberFormatOptions: Intl.NumberFormatOptions = {
			maximumFractionDigits: this.maxDecimalPlaces,
			minimumFractionDigits: this.minDecimalPlaces,
			useGrouping: false,
		};
		const formattedPermanentValue = new Intl.NumberFormat(
			LocaleService.locale,
			permanentNumberFormatOptions,
		).format(numberWithMaxDecimals);
		this.value = formattedPermanentValue;

		// Thousands separation and currency notation should only be applied when the input is blurred
		const blurNumberFormatOptions: Intl.NumberFormatOptions = {
			...permanentNumberFormatOptions,
			useGrouping: this.separateThousands,
			style: this.currency ? 'currency' : undefined,
			currency: this.currency,
		};
		const formattedBlurValue = new Intl.NumberFormat(LocaleService.locale, blurNumberFormatOptions).format(
			numberWithMaxDecimals,
		);
		return formattedBlurValue;
	}

	async componentDidLoad() {
		this.updateSeparators();

		// if separateThousands is not set, use DecimalFormatter as default
		if (this.separateThousands || this.maxDecimalPlaces > 0 || this.minDecimalPlaces > 0) {
			this.inputElement.addViewValueFormatter(this.applyDecimalFormatter);
		}

		this.inputElement.addEventListener('zdsValidate', (event) => {
			const parsedValue = Number((event.detail.value as string).replace(',', '.'));

			if (parsedValue < this.min) {
				event.detail.validationState = {
					message: getDefaultMessage(ValidationReasons.RangeUnderflow),
					valid: false,
					reason: ValidationReasons.RangeUnderflow,
				};
			} else if (parsedValue > this.max) {
				event.detail.validationState = {
					message: getDefaultMessage(ValidationReasons.RangeOverflow),
					valid: false,
					reason: ValidationReasons.RangeOverflow,
				};
			} else {
				event.detail.validationState = {
					message: '',
					valid: true,
					reason: null,
				};
			}
		});

		this.htmlInputElement = await this.inputElement.getInputElement();
	}

	@Bind
	handleKeyDown(event: KeyboardEvent) {
		// Ignore key presses in some cases
		if (!this.isAllowedKeyDown(event) && !this.isAllowedMinusKeyDown(event)) {
			event.preventDefault();
		}

		// Increment/decrement using up/down keys
		if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
			let parsedValue = parseInt(this.value);
			if (isNaN(parsedValue)) {
				parsedValue = 0;
			}

			let newValue = event.key === 'ArrowDown' ? parsedValue - 1 : parsedValue + 1;
			if (this.min !== undefined) {
				newValue = Math.max(this.min, newValue);
			}
			if (this.max !== undefined) {
				newValue = Math.min(newValue, this.max);
			}

			const oldValue = this.value;
			this.value = newValue.toString();

			if (oldValue !== this.value) {
				this.zdsInput.emit(this.value);
			}
		}

		// If the user types the thousands separator, they likely mean a decimal separator, so replace it
		if (event.key === this.thousandSeparator) {
			const charIndex = this.htmlInputElement.selectionStart;
			event.preventDefault();
			this.value = this.value.substring(0, charIndex) + this.decimalSeparator + this.value.substring(charIndex);
			this.htmlInputElement.setSelectionRange(charIndex + 1, charIndex + 1);
		}
	}

	@Bind
	handlePaste(event: ClipboardEvent) {
		event.preventDefault();
		const pastedData = event.clipboardData.getData('text');
		const sanitizedData = pastedData.replace(/\D/g, '');
		this.value = sanitizedData;

		onGenericSubInputPaste(this);
	}

	isAllowedKeyDown(event: KeyboardEvent) {
		// allow numerical keys
		if (/^\d$/.test(event.key)) {
			return true;
		}

		// allow any keydown events modified by control or alt (ie for pasting)
		if (event.ctrlKey || event.metaKey) {
			return true;
		}

		// allow any other modifier that makes sense for navigating or deleting
		if (
			['arrowleft', 'arrowright', 'backspace', 'delete', 'end', 'enter', 'home', 'tab'].includes(
				event.key.toLowerCase(),
			)
		) {
			return true;
		}

		// if maxDecimalPlaces is greater than 0 we allow ',' and '.'
		if (this.maxDecimalPlaces > 0 && (event.key === ',' || event.key === '.')) {
			return true;
		}

		return false;
	}

	isAllowedMinusKeyDown(event: KeyboardEvent) {
		// do not test any other keys than hypthen
		if (event.key !== '-') {
			return false;
		}

		const inputElement = this.inputElement.shadowRoot.querySelector('input');

		// allow hyphen when all text is selected
		if (inputElement.selectionStart === 0 && inputElement.selectionEnd === inputElement.value.length) {
			return true;
		}

		// allow hyphen when the cursor is at the start and the input does not contain a hyphen yet
		if (inputElement.selectionStart === 0 && !inputElement.value.startsWith('-')) {
			return true;
		}

		return false;
	}

	isNumeric(value: unknown) {
		return `${+value}` === value;
	}

	parseFormattedNumber(numberString: string) {
		let parsedNumberString = numberString;

		// Remove thousands separator
		parsedNumberString = parsedNumberString.replace(new RegExp(`\\${this.thousandSeparator}`, 'g'), '');

		// Use period as decimal separator
		parsedNumberString = parsedNumberString.replace(new RegExp(`\\${this.decimalSeparator}`), '.');

		const parsedNumber = parseFloat(parsedNumberString);

		if (Number.isNaN(parsedNumber)) return null;
		return parsedNumber;
	}

	render() {
		return (
			<zds-input-text
				autocomplete={this.autocomplete}
				componentTabIndex={this.componentTabIndex}
				class="zds-input-number__input"
				clearable={this.clearable}
				disabled={this.disabled}
				fitHeight={this.fitHeight}
				forceInvalid={this.forceInvalid}
				includeInForm={this.includeInForm}
				inputAria={this.inputAria}
				inputmode="decimal"
				instantSave={this.instantSave}
				label={this.label}
				maxlength={this.maxlength}
				minlength={this.minlength}
				name={this.name}
				onKeyDown={this.handleKeyDown}
				onPaste={this.handlePaste}
				onZdsChange={() => (this.value = this.inputElement.value)}
				onZdsInput={() => (this.value = this.inputElement.value)}
				noAsterisk={this.noAsterisk}
				pattern={this.pattern}
				placeholder={this.placeholder}
				plaintext={this.plaintext}
				readonly={this.readonly}
				ref={(element) => (this.inputElement = element)}
				required={this.required}
				silentErrors={this.silentErrors}
				type="text"
				value={this.value}
				zdsId={this.zdsId}
			>
				<slot name="content-start" slot="content-start"></slot>
				<slot name="input-start" slot="input-start"></slot>
				<slot name="input-end" slot="input-end"></slot>
				<slot name="content-end" slot="content-end"></slot>
				<slot name="label" slot="label"></slot>
				<slot name="validation-message" slot="validation-message"></slot>
			</zds-input-text>
		);
	}

	@Bind
	updateSeparators() {
		this.thousandSeparator = Intl.NumberFormat(LocaleService.locale).format(11111).replace(/\d/g, '');
		this.decimalSeparator = Intl.NumberFormat(LocaleService.locale).format(1.1).replace(/\d/g, '');
	}
}
